import React, { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import './LoginPage.css';

const LoginPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoSrc, setLogoSrc] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();

  useEffect(() => {
    // Load website config from API with localStorage fallback
    const loadConfig = async () => {
      try {
        const resp = await api.getWebsiteConfig();
        const cfg = resp.data || {};
        const isLightTheme = document.body.classList.contains('light-theme');
        const src = isLightTheme ? (cfg.logoDarkDataUrl || cfg.logoLightDataUrl) : (cfg.logoLightDataUrl || cfg.logoDarkDataUrl);
        if (src) setLogoSrc(src);
        return;
      } catch (err) {
        // ignore and try localStorage
      }

      try {
        const stored = localStorage.getItem('websiteConfigData');
        if (stored) {
          const parsed = JSON.parse(stored);
          const isLightTheme = document.body.classList.contains('light-theme');
          const src = isLightTheme ? (parsed.logoDarkDataUrl || parsed.logoLightDataUrl) : (parsed.logoLightDataUrl || parsed.logoDarkDataUrl);
          if (src) setLogoSrc(src);
        }
      } catch (err) {
        // ignore
      }
    };

    loadConfig();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.login(password);
      api.setToken(response.data.token);
      login(response.data.token);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-icon">
            {logoSrc ? (
              <img src={logoSrc} alt="logo" className="login-logo" />
            ) : (
              <Lock size={32} />
            )}
          </div>
          <h1>AC EVO Server Configurator</h1>
          <p>Enter password to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group password-wrapper">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              autoFocus
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" disabled={loading} className="login-button">
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
